import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, Target } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { ROUTES } from '../../../utils/constants';
import type { CanDoLookup } from '../../../components/student/studentLessonProgressFriendly';
import {
  friendlyRecommendation,
  partitionSkills,
  skillDisplayTitle,
} from '../../../components/student/studentLessonProgressFriendly';
import type { StudentProgressAnalyticsOut } from '../../tutor/api/tutor.client';
import {
  DashboardCardSkeleton,
  DashboardEmptyState,
  DashboardInlineError,
} from './dashboardUi';
import { useTutorTenantLabels } from '../../tutor/hooks/useTutorTenantLabels';

const BUTTON_GRADIENT =
  'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 hover:brightness-110 shadow-lg shadow-purple-500/25';

type Props = {
  data: StudentProgressAnalyticsOut | null;
  canDoLookup: CanDoLookup;
  loading: boolean;
  error: string | null;
  enabled: boolean;
  onRetry?: () => void;
};

export function AiRecommendationCard({
  data,
  canDoLookup,
  loading,
  error,
  enabled,
  onRetry,
}: Props) {
  const { t } = useTranslation();
  const { labels } = useTutorTenantLabels();
  const { topTip, weakArea } = useMemo(() => {
    if (!data) return { topTip: null as string | null, weakArea: null as string | null };

    const tips = (data.recommendations || []).map((r) => friendlyRecommendation(r, canDoLookup));
    const topTip = tips[0] ?? null;

    const seen = new Set<string>();
    const rows: Array<Record<string, unknown>> = [];
    for (const row of [...(data.strengths || []), ...(data.weaknesses || [])]) {
      const id = String(row._id ?? row.can_do_id ?? '');
      if (!id || seen.has(id)) continue;
      seen.add(id);
      rows.push(row);
    }
    const { focus } = partitionSkills(rows);
    const weakArea = focus[0] ? skillDisplayTitle(focus[0], canDoLookup).title : null;

    return { topTip, weakArea };
  }, [data, canDoLookup]);

  if (!enabled) return null;

  if (loading) {
    return <DashboardCardSkeleton className="h-full" />;
  }

  if (error) {
    return (
      <Card className="border-purple-200/60 dark:border-purple-500/25 h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-slate-900 dark:text-white">
            <Sparkles className="h-5 w-5 text-purple-600 dark:text-pink-400" aria-hidden />
            {t('dashboard.studentHome.aiRecommendations')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DashboardInlineError message={error} onRetry={onRetry} />
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card className="border-purple-200/60 dark:border-purple-500/25 h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-slate-900 dark:text-white">
            <Sparkles className="h-5 w-5 text-purple-600 dark:text-pink-400" aria-hidden />
            {t('dashboard.studentHome.aiRecommendations')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <DashboardEmptyState>
            {labels.recommendationEmptyHint}
          </DashboardEmptyState>
          <Link
            to={ROUTES.STUDENT_TAMIL_TUTOR}
            className={`inline-flex min-h-[44px] items-center justify-center rounded-full px-6 text-sm font-bold text-white ${BUTTON_GRADIENT}`}
          >
            {labels.practiceCta}
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-purple-200/60 bg-gradient-to-br from-white to-purple-50/40 dark:border-purple-500/25 dark:from-gray-800 dark:to-purple-950/20 h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2 text-slate-900 dark:text-white">
          <Sparkles className="h-5 w-5 text-purple-600 dark:text-pink-400" aria-hidden />
          AI recommendations
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {topTip ? (
          <div className="rounded-xl border border-indigo-200/80 bg-indigo-50/60 dark:border-indigo-800/50 dark:bg-indigo-950/20 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300 mb-1">
              {t('dashboard.studentHome.topRecommendation')}
            </p>
            <p className="text-sm text-slate-800 dark:text-slate-200">{topTip}</p>
          </div>
        ) : (
          <DashboardEmptyState className="py-6">
            {labels.keepPractisingHint}
          </DashboardEmptyState>
        )}
        {weakArea ? (
          <div className="flex items-start gap-2 rounded-xl border border-amber-200/80 bg-amber-50/60 dark:border-amber-800/50 dark:bg-amber-950/20 px-4 py-3">
            <Target className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" aria-hidden />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300">
                {t('dashboard.studentHome.weakArea')}
              </p>
              <p className="text-sm font-medium text-slate-900 dark:text-white">{weakArea}</p>
            </div>
          </div>
        ) : null}
        <Link
          to={ROUTES.STUDENT_TAMIL_TUTOR}
          className={`inline-flex min-h-[44px] w-full sm:w-auto items-center justify-center rounded-full px-6 text-sm font-bold text-white ${BUTTON_GRADIENT}`}
        >
          {t('dashboard.studentHome.practiceNow')}
        </Link>
      </CardContent>
    </Card>
  );
}
